/**
 * Ad engagement tracker.
 *
 * The carousel cycles every 15 seconds, so a staff member who leaves the
 * dashboard open all day generates on the order of two thousand renders. Sending
 * one request per render would be absurd; instead this counts renders in memory
 * and posts aggregated counters on tab-hide, with a 30-minute safety timer
 * behind it. That works out at roughly sixteen requests per user per day.
 *
 * Three properties matter and are easy to break:
 *
 *   - Nothing is counted unless it was genuinely visible (half on screen, two
 *     seconds, tab in the foreground). A background tab counts nothing.
 *   - Counters survive a crashed tab, via a localStorage mirror replayed on the
 *     next load.
 *   - A flush carries a batch id, so a retry after a lost response does not
 *     double-count on the server.
 *
 * Failures are deliberately invisible to clinic staff: this is our telemetry,
 * not their problem.
 */

const STORAGE_KEY = "dentcloud_ad_counters";
const SAFETY_INTERVAL_MS = 30 * 60 * 1000;
const VIEWABLE_RATIO = 0.5;
const VIEWABLE_DWELL_MS = 2000;
/** Buffered counters older than this are dropped rather than posted. */
const MAX_BUFFER_AGE_MS = 24 * 60 * 60 * 1000;

const API_BASE_URL =
  process.env.NODE_ENV === "local"
    ? `http://${window.location.hostname}:5001`
    : process.env.VUE_APP_API_BASE_URL;

const counterKey = (adRef, placement) => `${adRef}|${placement}`;

const newBatchId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  // Older Safari: any collision-resistant string will do, the server only uses
  // it for equality.
  return `b-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

class AdTracker {
  constructor() {
    /** @type {Map<string, {adRef: string, placement: string, count: number}>} */
    this.counters = new Map();
    /** @type {{adRef: string, placement: string, clickedAt: string, targetUrl: string}[]} */
    this.clicks = [];
    /** Oldest unsent counter, used to discard a stale buffer. */
    this.bufferedSince = null;
    this.context = null;
    this.observer = null;
    /** @type {Map<Element, {adRef: string, placement: string, timer: number|null}>} */
    this.observed = new Map();
    this.safetyTimer = null;
    this.listenersAttached = false;
    this.flushing = false;

    this.restore();
  }

  /**
   * @param {{clinicURL: string, branchURL: string}} context which branch the
   *   events belong to; the server re-derives everything else from the session.
   */
  setContext(context) {
    this.context = context;
    this.attachListeners();
  }

  attachListeners() {
    if (this.listenersAttached || typeof window === "undefined") return;

    this.onVisibilityChange = () => {
      if (document.visibilityState === "hidden") this.flush();
    };
    this.onPageHide = () => this.flush();

    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("pagehide", this.onPageHide);
    this.safetyTimer = window.setInterval(() => this.flush(), SAFETY_INTERVAL_MS);
    this.listenersAttached = true;
  }

  teardown() {
    if (!this.listenersAttached) return;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("pagehide", this.onPageHide);
    window.clearInterval(this.safetyTimer);
    this.listenersAttached = false;
    this.unobserveAll();
  }

  /**
   * Watch an element and count an impression once it has been genuinely
   * visible. Re-observing the same element replaces the previous registration,
   * which is what happens as the carousel swaps ads through one slide element.
   */
  observe(element, adRef, placement) {
    if (!element || !adRef) return;
    if (!("IntersectionObserver" in window)) {
      // No viewability signal available: count it once on registration rather
      // than not at all.
      this.trackImpression(adRef, placement);
      return;
    }

    if (!this.observer) {
      this.observer = new IntersectionObserver(
        (entries) => this.onIntersect(entries),
        { threshold: [VIEWABLE_RATIO] }
      );
    }

    this.clearDwell(element);
    this.observed.set(element, { adRef, placement, timer: null });
    this.observer.observe(element);
  }

  unobserve(element) {
    if (!element) return;
    this.clearDwell(element);
    this.observed.delete(element);
    if (this.observer) this.observer.unobserve(element);
  }

  unobserveAll() {
    for (const element of [...this.observed.keys()]) this.unobserve(element);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  clearDwell(element) {
    const entry = this.observed.get(element);
    if (entry?.timer) {
      window.clearTimeout(entry.timer);
      entry.timer = null;
    }
  }

  onIntersect(entries) {
    for (const entry of entries) {
      const registration = this.observed.get(entry.target);
      if (!registration) continue;

      const visible =
        entry.isIntersecting &&
        entry.intersectionRatio >= VIEWABLE_RATIO &&
        document.visibilityState === "visible";

      if (!visible) {
        this.clearDwell(entry.target);
        continue;
      }

      if (registration.timer) continue;

      // Dwell: an ad that flickers past during a slide transition is not an
      // impression.
      registration.timer = window.setTimeout(() => {
        registration.timer = null;
        if (document.visibilityState !== "visible") return;
        this.trackImpression(registration.adRef, registration.placement);
      }, VIEWABLE_DWELL_MS);
    }
  }

  /** @param {string} adRef @param {"carousel"|"popup"} placement */
  trackImpression(adRef, placement = "carousel") {
    if (!adRef) return;
    const key = counterKey(adRef, placement);
    const entry = this.counters.get(key) || { adRef, placement, count: 0 };
    entry.count += 1;
    this.counters.set(key, entry);
    if (!this.bufferedSince) this.bufferedSince = Date.now();
    this.persist();
  }

  /** @param {string} adRef @param {"carousel"|"popup"} placement @param {string} targetUrl */
  trackClick(adRef, placement = "carousel", targetUrl = "") {
    if (!adRef) return;
    this.clicks.push({
      adRef,
      placement,
      clickedAt: new Date().toISOString(),
      targetUrl: String(targetUrl || "").slice(0, 1024),
    });
    if (!this.bufferedSince) this.bufferedSince = Date.now();
    this.persist();
  }

  hasPending() {
    return this.counters.size > 0 || this.clicks.length > 0;
  }

  persist() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          bufferedSince: this.bufferedSince,
          counters: [...this.counters.values()],
          clicks: this.clicks,
        })
      );
    } catch {
      // Private mode or a full quota: the in-memory counters still flush.
    }
  }

  restore() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);

      // A buffer this old means the endpoint has been failing for a day; those
      // counters are no longer worth the storage, let alone the request.
      if (
        saved.bufferedSince &&
        Date.now() - saved.bufferedSince > MAX_BUFFER_AGE_MS
      ) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      this.bufferedSince = saved.bufferedSince || null;
      for (const entry of saved.counters || []) {
        this.counters.set(counterKey(entry.adRef, entry.placement), entry);
      }
      this.clicks = saved.clicks || [];
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  clearBuffer() {
    this.counters.clear();
    this.clicks = [];
    this.bufferedSince = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to do; the in-memory state is already clear.
    }
  }

  /**
   * Post whatever has accumulated. Silent by design — a failed flush keeps the
   * counters for the next attempt and tells the user nothing.
   */
  async flush() {
    if (this.flushing || !this.hasPending() || !this.context) return;

    const token = localStorage.getItem("access_token");
    if (!token) return;

    const { clinicURL, branchURL } = this.context;
    if (!clinicURL || !branchURL) return;

    const payload = {
      batchId: newBatchId(),
      impressions: [...this.counters.values()],
      clicks: this.clicks,
    };

    this.flushing = true;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/adEvent/${clinicURL}/${branchURL}`,
        {
          method: "POST",
          // keepalive, not sendBeacon: a beacon cannot carry the Authorization
          // header, and this request survives page unload.
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        this.clearBuffer();
      } else if (response.status >= 400 && response.status < 500) {
        // The server will never accept this payload. Keeping it would retry it
        // forever, so drop it — losing one flush beats a permanent loop.
        this.clearBuffer();
      }
      // 5xx: keep the buffer and try again on the next flush.
    } catch {
      // Offline or unloading: keep the buffer.
    } finally {
      this.flushing = false;
    }
  }
}

/**
 * Pull the branch context out of a route.
 *
 * The router declares these params as `clinicUrl`/`branchUrl`, while the API
 * paths and most services spell them `clinicURL`/`branchURL`. Accepting both
 * spellings here keeps that inconsistency from silently disabling tracking.
 *
 * @param {{params?: object}} route
 * @returns {{clinicURL: string, branchURL: string}}
 */
export function adContextFromRoute(route) {
  const params = route?.params || {};
  return {
    clinicURL: params.clinicUrl || params.clinicURL,
    branchURL: params.branchUrl || params.branchURL,
  };
}

let tracker = null;

/**
 * @returns {AdTracker} the shared tracker; one per tab, deliberately.
 */
export function useAdTracker() {
  if (!tracker) tracker = new AdTracker();
  return tracker;
}

export default useAdTracker;
